/**
 * Epic AB#1824 — Concurrency banner shown on 409 Hours.Stale (Dina §G).
 *
 * Surfaces above the matrix when the BE rejected the bulk-write because
 * another owner updated the same cells. Provides two CTAs:
 *   - "Review changes" — keeps the local draft but re-fetches server state
 *     so the owner can compare cell-by-cell (the matrix highlights the
 *     conflicted cells red via cellState.isStale).
 *   - "Discard my draft" — wipes the local edits, banner dismisses.
 *
 * Save is disabled while the banner is visible.
 */
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate, pluralize } from './interpolate';
import type { HoursStaleErrorDetails } from '../../types/api-responses';
import styles from './BusinessHoursMatrix.module.css';

export interface ConcurrencyBannerProps {
    details: HoursStaleErrorDetails;
    /**
     * Optional resolved branch name for the scope sentence. Caller passes
     * the affected branch's display name; we fall back to "{count} branches"
     * when the conflict touches multiple branches.
     */
    branchName?: string;
    onReview: () => void;
    onDiscard: () => void;
    onDismiss: () => void;
}

/**
 * Lightweight "time ago" formatter — falls back to "moments ago" when we
 * can't parse the timestamp. Sufficient for a non-realtime banner; if the
 * matrix needs sub-minute precision we can swap this for date-fns.
 */
function timeAgo(iso: string | undefined): string {
    if (!iso) return 'moments ago';
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return 'moments ago';
    const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hours ago`;
    return `${Math.round(seconds / 86400)} days ago`;
}

const ConcurrencyBanner: React.FC<ConcurrencyBannerProps> = ({
    details,
    branchName,
    onReview,
    onDiscard,
    onDismiss,
}) => {
    const { t } = useLanguage();

    const editorName = details.lastEditedBy ?? '';
    const conflictedBranchIds = new Set(
        (details.conflictedCells ?? []).map((c) => c.branchId),
    );
    const branchCount = conflictedBranchIds.size;

    const branchScope = branchName
        ? interpolate(t('hoursMatrix.banner.concurrency.scope.branch'), { branch: branchName })
        : pluralize(t, 'hoursMatrix.banner.concurrency.scope.multiple', branchCount || 1, {
            count: branchCount || 1,
        });

    // Pick generic vs personalised body depending on whether the BE provided
    // the editor name (it may not — only the multi-tab case has it reliably).
    const body = editorName
        ? interpolate(t('hoursMatrix.banner.concurrency.body'), {
            editorName,
            branchScope,
            timeAgo: timeAgo(details.lastEditedAt),
        })
        : t('hoursMatrix.banner.concurrency.bodyGeneric');

    return (
        <div className={styles.banner} role="alert" aria-label={t('hoursMatrix.a11y.banner')}>
            <div className={styles.bannerGlyph} aria-hidden="true">⚠</div>
            <div className={styles.bannerBody}>
                <div className={styles.bannerTitle}>
                    {t('hoursMatrix.banner.concurrency.title')}
                </div>
                <div className={styles.bannerText}>{body}</div>
                <div className={styles.bannerActions}>
                    <button
                        type="button"
                        className={`${styles.bannerActionBtn} ${styles.bannerActionBtnPrimary}`}
                        onClick={onReview}
                    >
                        {t('hoursMatrix.banner.concurrency.review')}
                    </button>
                    <button
                        type="button"
                        className={styles.bannerActionBtn}
                        onClick={onDiscard}
                    >
                        {t('hoursMatrix.banner.concurrency.discard')}
                    </button>
                </div>
            </div>
            <button
                type="button"
                className={styles.bannerClose}
                onClick={onDismiss}
                aria-label={t('hoursMatrix.banner.concurrency.dismiss')}
            >
                ✕
            </button>
        </div>
    );
};

export default ConcurrencyBanner;
